/**
 * The shell: sign in, then one of the screens next to the rail. `/` is the map of
 * the workspace the account starts in (MA-72): Huizen, or SportCity.
 */
import { lazy, Suspense, useEffect } from 'react';
import { Laadbalk } from './components/Laadbalk';
import { Rail } from './components/Rail';
import { isStoring } from './lib/api/client';
import { useSessie } from './lib/api/queries';
import { navigeer, ROUTES, SCHERMEN, usePad, useUrl } from './lib/router';
import {
  isGedeeldPad,
  onthoudWerkruimte,
  startpad,
  voorkeurWerkruimte,
  werkruimteVanPad,
} from './lib/werkruimte';
import { DatasetsScherm } from './screens/datasets/DatasetsScherm';
import { Inloggen } from './screens/Inloggen';
import { InstellingenScherm } from './screens/instellingen/InstellingenScherm';
import { LabelsScherm } from './screens/labels/LabelsScherm';
import { NaInloggen } from './screens/NaInloggen';
import { Storing } from './screens/Storing';

/**
 * De kaartbibliotheek is een megabyte op zichzelf. Het datasets-scherm heeft hem
 * niet nodig, dus komt hij pas binnen als de kaart in beeld komt.
 */
const KaartScherm = lazy(() =>
  import('./screens/map/KaartScherm').then((module) => ({ default: module.KaartScherm })),
);

/**
 * De mobiele app (de Funda-ronde) laadt los: wie hem op de telefoon opent, heeft
 * aan de kaart en de tabellen niets.
 */
const MobieleRonde = lazy(() =>
  import('./mobiel/MobieleRonde').then((module) => ({ default: module.MobieleRonde })),
);

/** Kansjes tekent zijn eigen kaartje en hangt dus aan dezelfde megabyte. */
const KansjesScherm = lazy(() =>
  import('./screens/kansjes/KansjesScherm').then((module) => ({ default: module.KansjesScherm })),
);

/** Het bord met de fasen; tekent net als Kansjes een kaartje, en 3D. */
const KanbanScherm = lazy(() =>
  import('./screens/kanban/KanbanScherm').then((module) => ({ default: module.KanbanScherm })),
);

/** De werkruimte SportCity (MA-72): de clubs en de plaatsen op een eigen kaart. */
const SportcityScherm = lazy(() =>
  import('./screens/sportcity/SportcityScherm').then((module) => ({
    default: module.SportcityScherm,
  })),
);

/** Een woning taxeren met referenties (MA-56); tekent een eigen kaartje. */
const TaxatieScherm = lazy(() =>
  import('./screens/taxatie/TaxatieScherm').then((module) => ({ default: module.TaxatieScherm })),
);

const TITELS: Record<string, string> = {
  [ROUTES.taxatie]: 'Taxatie · MamiMap',
  [ROUTES.datasets]: 'Datasets · MamiMap',
  [ROUTES.kansjes]: 'Kansjes · MamiMap',
  [ROUTES.kanban]: 'Kanban · MamiMap',
  [ROUTES.labels]: 'Labels · MamiMap',
  [ROUTES.instellingen]: 'Instellingen · MamiMap',
  [ROUTES.ronde]: 'Funda-ronde · MamiMap',
  [ROUTES.kaart]: 'Kaart · MamiMap',
  [ROUTES.sportcity]: 'SportCity · MamiMap',
};

export default function App() {
  const sessie = useSessie();
  const pad = usePad();
  const url = useUrl();

  /**
   * Alle schermen heetten MamiMap in het tabblad, en twee tabbladen naast elkaar
   * waren dan niet uit elkaar te houden. De schermnaam gaat voorop, de app erachter.
   */
  const ingelogd = sessie.data?.ingelogd === true;
  useEffect(() => {
    document.title = ingelogd ? (TITELS[pad] ?? TITELS[ROUTES.kaart] ?? 'MamiMap') : 'MamiMap';
  }, [pad, ingelogd]);

  /*
   * Waar dit account mag kijken (MA-72). Een pad in een werkruimte waar het niet in
   * zit, gaat naar het begin van een werkruimte waar het wel in zit; Instellingen en
   * inloggen zijn van iedereen.
   */
  const werkruimtes = sessie.data?.gebruiker?.werkruimtes ?? null;
  const voorkeur = werkruimtes ? voorkeurWerkruimte(werkruimtes) : null;
  const toegestaan =
    !werkruimtes || isGedeeldPad(pad) || werkruimtes.includes(werkruimteVanPad(pad));

  useEffect(() => {
    const bekend =
      pad === ROUTES.instellingen ||
      pad === ROUTES.ronde ||
      pad === ROUTES.taxatie ||
      pad === ROUTES.labels ||
      pad === ROUTES.inloggen ||
      pad === ROUTES.sportcity ||
      SCHERMEN.some((route) => route === pad);
    if (bekend) return;
    const query = url.split('?')[1];
    const naar = voorkeur ? startpad(voorkeur) : ROUTES.kaart;
    navigeer(query && naar === ROUTES.kaart ? `${naar}?${query}` : naar, { vervang: true });
  }, [pad, url, voorkeur]);

  useEffect(() => {
    if (!ingelogd || !werkruimtes) return;
    if (!toegestaan) {
      if (voorkeur) navigeer(startpad(voorkeur), { vervang: true });
      return;
    }
    if (!isGedeeldPad(pad)) onthoudWerkruimte(werkruimteVanPad(pad));
  }, [ingelogd, werkruimtes, toegestaan, voorkeur, pad]);

  // A blank page while the session is still unknown: no flash of a login form.
  if (sessie.isPending) return <div className="h-full bg-paper" />;
  // Een storing is geen uitlogging: wie de database mist, krijgt de melding die de
  // API geeft, niet het inlogformulier.
  if (!sessie.data && isStoring(sessie.error)) {
    return (
      <Storing
        fout={sessie.error}
        bezig={sessie.isFetching}
        opnieuw={() => void sessie.refetch()}
      />
    );
  }
  if (!sessie.data?.ingelogd || !sessie.data.gebruiker) return <Inloggen />;

  /* Ingelogd op /inloggen: door naar ?terug= (alleen mamimap.nl), anders de kaart. */
  if (pad === ROUTES.inloggen) return <NaInloggen naar={startpad(voorkeur ?? 'huizen')} />;

  /* Even niets tonen tot het pad is omgezet: een Huizen-scherm zou meteen gaan vragen. */
  if (!toegestaan) return <div className="h-full bg-paper" />;

  /* De mobiele app: eigen schil, geen rail. */
  if (pad === ROUTES.ronde) {
    return (
      <Suspense fallback={<div className="h-full bg-paper" />}>
        <MobieleRonde />
      </Suspense>
    );
  }

  return (
    <div className="flex h-full">
      <Rail werkruimtes={werkruimtes ?? ['huizen']} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Laadbalk />
        {pad === ROUTES.datasets ? (
          <DatasetsScherm />
        ) : pad === ROUTES.instellingen ? (
          <InstellingenScherm sessie={sessie.data} />
        ) : pad === ROUTES.labels ? (
          <LabelsScherm />
        ) : (
          <Suspense fallback={<div className="min-h-0 flex-1 bg-paper" />}>
            {pad === ROUTES.sportcity ? (
              <SportcityScherm />
            ) : pad === ROUTES.kansjes ? (
              <KansjesScherm />
            ) : pad === ROUTES.kanban ? (
              <KanbanScherm />
            ) : pad === ROUTES.taxatie ? (
              <TaxatieScherm />
            ) : (
              <KaartScherm />
            )}
          </Suspense>
        )}
      </div>
    </div>
  );
}
